/**
 * UTLÄGG — det du lagt ut ur egen ficka.
 *
 * TVÅ REGLER SOM KOSTAR PENGAR OM DE BRYTS:
 *
 *   1. BELOPPET ÄR EX MOMS. Kvittots totalsumma är INKL moms. Skrivs den in här
 *      vidarefaktureras varje utlägg 25 % för högt — systematiskt och osynligt,
 *      för ingenting kraschar. schema/company.sql säger rakt ut att klientens
 *      fält ska heta "Summa exkl. moms" och att montören läser av momsraden på
 *      kvittot. Det är precis vad fältet nedan heter.
 *
 *   2. VIDAREFAKTURERAS DET MÅSTE DET HÖRA TILL ETT JOBB. CHECK (billable = 0 OR
 *      work_order_id IS NOT NULL) i schemat. Väljaren nedan slår därför på
 *      arbetsordern så fort "Vidarefaktureras" är valt, i stället för att låta
 *      raden falla på ett anonymt VALIDATION_FAILED.
 *
 * BESLUT 13: INGEN KVITTO-AI. Bilden är en bild och summan skrivs för hand.
 * FILE_UPLOAD svarar 501 i workern i dag, så "Fota kvitto" säger sanningen om
 * det i stället för att krascha — och raden går att spara utan foto, vilket är
 * hela poängen med att object_key genereras på klienten: bilden kan komma efteråt.
 *
 * BELOPPET SYNS FÖR ALLA. Det är inget timpris: ExpenseWorkerRow behåller
 * amount_ore med flit (beslut 16 skalar bort satser, inte den anställdes egna
 * utlägg — den som lagt ut pengar måste se hur mycket).
 */

import { useMemo, useState } from 'react';
import { asOre } from '@delat/types';
import type { ExpenseWorkerRow, SyncOp, WorkDate } from '@delat/types';
import {
  EjByggd,
  FAKTOR,
  Falt,
  Felruta,
  Knapp,
  Laddar,
  Listrad,
  Sifferblock,
  Snabbrad,
  Talfalt,
  Tomtext,
  Valfalt,
  Veckonavigering,
  Yta,
  tillHeltal,
} from '../ui';
import {
  dagarIVecka,
  idag,
  plusDagar,
  veckaFor,
  visaDag,
  visaOren,
  visaVecka,
} from '../app/datum';
import { MAX_BESKRIVNING_TECKEN, MAX_ORE_PER_UTLAGG } from '../api/konstanter';
import { useInloggad } from '../app/session';
import { useSynk } from './tidrapport/lager';
import type { Ogonblicksbild, Synk } from './tidrapport/lager';
import { felAvOperation, useSparning } from './tidrapport/spara';
import { nyttUtlaggsradId, raderaUtlaggOp, utlaggOp } from './tidrapport/skriv';
import {
  VECKOLAGETEXT,
  farRapportera,
  arbetsdagUrFalt,
  farSkrivaVecka,
  inomArbetsdagsfonstret,
  orderMedId,
  ordernamn,
  senastArbetsdag,
  sorteradeOrdrar,
  tidigastArbetsdag,
  veckansLage,
} from './tidrapport/regler';

/* Schemats CHECK-gränser står på ETT ställe i klienten: api/konstanter.ts. */
const MAX_ORE = MAX_ORE_PER_UTLAGG;
const MAX_BESKRIVNING = MAX_BESKRIVNING_TECKEN;

type Vidarefaktureras = 'ja' | 'nej';

export const Utlagg = () => {
  const { firmaId } = useInloggad();
  const synk = useSynk(firmaId);

  if (synk.bild === null) {
    return synk.fel === null ? (
      <Laddar rader={4} />
    ) : (
      <Felruta
        fel={synk.fel}
        atgard={
          <Knapp variant="piller-mini" onClick={() => void synk.synka(true)}>
            Försök igen
          </Knapp>
        }
      />
    );
  }
  return <Utlaggsskarmen bild={synk.bild} synk={synk} />;
};

const Utlaggsskarmen = ({
  bild,
  synk,
}: {
  readonly bild: Ogonblicksbild;
  readonly synk: Synk;
}) => {
  const { konto, roll, arChef, arLast } = useInloggad();
  const sparning = useSparning(synk.skicka);

  const [ankare, sattAnkare] = useState<WorkDate>(() => idag());
  const [oppet, sattOppet] = useState(false);
  const [visaFotoBesked, sattVisaFotoBesked] = useState(false);
  const [beskrivning, sattBeskrivning] = useState('');
  const [kronor, sattKronor] = useState('');
  const [vidare, sattVidare] = useState<Vidarefaktureras>('ja');
  const [orderId, sattOrderId] = useState<string | null>(null);
  const [dag, sattDag] = useState<WorkDate>(() => idag());
  const [markerad, sattMarkerad] = useState<string | null>(null);

  const vecka = veckaFor(ankare);
  const veckansDagar = dagarIVecka(vecka);
  const ordrar = useMemo(() => sorteradeOrdrar(bild), [bild]);

  const minaRader = useMemo(
    () => bild.utlaggsrader.filter((rad) => rad.account_id === konto.id),
    [bild.utlaggsrader, konto.id],
  );

  const veckansRader = useMemo(
    () =>
      minaRader
        .filter((rad) => veckansDagar.includes(rad.work_date))
        .sort((a, b) =>
          a.work_date === b.work_date
            ? a.id.localeCompare(b.id)
            : a.work_date.localeCompare(b.work_date),
        ),
    [minaRader, veckansDagar],
  );

  const veckansOren = veckansRader.reduce((summa, rad) => summa + Number(rad.amount_ore), 0);

  const lage = veckansLage(bild, konto.id, veckaFor(dag));
  const farSkriva =
    farRapportera(roll) &&
    !arLast &&
    inomArbetsdagsfonstret(dag) &&
    farSkrivaVecka({ lage, egen: true, arChef });

  const oren = kronor.trim() === '' ? null : tillHeltal(kronor, FAKTOR.kronor_oren);
  const orenDuger = oren !== null && oren >= 1 && oren <= MAX_ORE;
  const beskrivningDuger =
    beskrivning.trim().length >= 1 && beskrivning.trim().length <= MAX_BESKRIVNING;
  const kraverOrder = vidare === 'ja';
  const orderDuger = !kraverOrder || orderMedId(bild, orderId) !== null;
  const kanSpara =
    farSkriva && orenDuger && beskrivningDuger && orderDuger && !sparning.sparar;

  const andra = <T,>(satt: (varde: T) => void) => (varde: T) => {
    sparning.nollstall();
    satt(varde);
  };

  const spara = async (): Promise<void> => {
    if (oren === null) return;
    const order = orderMedId(bild, orderId);
    if (kraverOrder && order === null) return;
    const klart = await sparning.spara((): readonly SyncOp[] => [
      utlaggOp({
        id: nyttUtlaggsradId(),
        account_id: konto.id,
        work_date: dag,
        amount_ore: asOre(oren),
        description: beskrivning.trim(),
        // FILE_UPLOAD är inte byggt än, så raden sparas utan foto. object_key
        // genereras på klienten när bilden tas, så fotot kan komma efteråt.
        receipt_object_key: null,
        billable: kraverOrder ? 1 : 0,
        work_order_id: kraverOrder && order !== null ? order.id : null,
        base_version: null,
      }),
    ]);
    if (klart) {
      sattBeskrivning('');
      sattKronor('');
      sattOppet(false);
      sattAnkare(dag);
    }
  };

  const taBort = (rad: ExpenseWorkerRow): void => {
    sattMarkerad(null);
    sparning.nollstall();
    void sparning.spara(() => [raderaUtlaggOp(rad.id, rad.version)]);
  };

  const markeradRad = veckansRader.find((rad) => rad.id === markerad) ?? null;
  const rapporterar = farRapportera(roll);

  return (
    <>
      <Veckonavigering
        vecka={vecka}
        summa={visaOren(veckansOren)}
        onForegaende={() => {
          sattAnkare(plusDagar(ankare, -7));
          sattMarkerad(null);
        }}
        onNasta={() => {
          sattAnkare(plusDagar(ankare, 7));
          sattMarkerad(null);
        }}
      />

      <Yta>
        <Sifferblock
          varde={visaOren(veckansOren)}
          under={`Dina utlägg ${visaVecka(vecka).toLowerCase()}, ex moms · ${
            veckansRader.length
          } ${veckansRader.length === 1 ? 'kvitto' : 'kvitton'}`}
        />
      </Yta>

      {rapporterar && !arLast && (
        <Knapp
          variant="stor"
          ikon="kamera"
          onClick={() => {
            sattVisaFotoBesked(true);
            sattOppet(true);
          }}
        >
          Fota kvitto
        </Knapp>
      )}
      {visaFotoBesked && (
        <>
          <EjByggd vad="Kvittofoton" />
          <Tomtext>
            Lägg in kvittot för hand så länge — summan är det som går ut på
            underlaget. Bilden kan läggas till efteråt när uppladdningen är byggd.
          </Tomtext>
        </>
      )}

      {/* EX MOMS EN GÅNG, I RÄKNINGEN, i stället för på varje rad: hela listan
          täcks utan att varje belopp blir en mening. */}
      <Yta rubrik={visaVecka(vecka)} rakning={`${veckansRader.length} st · ex moms`}>
        {veckansRader.map((rad) => (
          <Listrad
            key={rad.id}
            titel={rad.description}
            bi={
              rad.work_order_id === null
                ? visaDag(rad.work_date)
                : `${visaDag(rad.work_date)} · ${ordernamn(orderMedId(bild, rad.work_order_id))}`
            }
            under={
              rad.billable === 1
                ? 'Vidarefaktureras kunden'
                : 'Ersätts bara till dig — går inte ut på fakturan'
            }
            varde={visaOren(rad.amount_ore)}
            onClick={
              rapporterar && !arLast
                ? () => sattMarkerad(markerad === rad.id ? null : rad.id)
                : undefined
            }
          />
        ))}
        {veckansRader.length === 0 && <Tomtext>Inga utlägg den här veckan.</Tomtext>}
      </Yta>

      {markeradRad !== null && (
        <Snabbrad>
          <Knapp variant="snabb" onClick={() => sattMarkerad(null)}>
            Behåll
          </Knapp>
          <Knapp
            variant="snabb"
            disabled={sparning.sparar}
            onClick={() => taBort(markeradRad)}
          >
            Ta bort {visaOren(markeradRad.amount_ore)}
          </Knapp>
        </Snabbrad>
      )}

      {rapporterar && !arLast && !oppet && (
        <Knapp variant="stor-tyst" onClick={() => sattOppet(true)}>
          Lägg in ett utlägg
        </Knapp>
      )}

      {oppet && (
        <Yta rubrik="Nytt utlägg">
          <Falt
            etikett="Vad köpte du?"
            varde={beskrivning}
            onAndra={(text) => andra(sattBeskrivning)(text)}
            maxLength={MAX_BESKRIVNING}
            placeholder="Grossisten — rördelar"
          />
          <Talfalt
            etikett="Summa exkl. moms"
            varde={kronor}
            onAndra={(text) => andra(sattKronor)(text)}
            faktor={FAKTOR.kronor_oren}
            suffix="kr"
            placeholder="890"
            hjalp="Läs av momsraden på kvittot. Totalsumman är INKL moms och är fel här."
            fel={
              kronor.trim() !== '' && !orenDuger
                ? 'Skriv summan i kronor, högst 1 000 000 kr.'
                : undefined
            }
          />
          <Valfalt
            etikett="Vidarefaktureras kunden?"
            varde={vidare}
            onAndra={(v) => andra(sattVidare)(v)}
            val={[
              { varde: 'ja' as const, etikett: 'Ja — läggs på jobbet' },
              { varde: 'nej' as const, etikett: 'Nej — bara tillbaka till mig' },
            ]}
            hjalp="Ett vidarefakturerat utlägg måste höra till en arbetsorder."
          />
          {kraverOrder && (
            <Valfalt
              etikett="Jobbet"
              varde={orderId ?? ''}
              onAndra={(id) => andra(sattOrderId)(id)}
              val={[
                { varde: '', etikett: 'Välj jobb ...' },
                ...ordrar.map((o) => ({ varde: String(o.id), etikett: ordernamn(o) })),
              ]}
            />
          )}
          <Falt
            etikett="Dagen"
            type="date"
            varde={dag}
            min={tidigastArbetsdag()}
            max={senastArbetsdag()}
            onAndra={(text) => {
              const vald = arbetsdagUrFalt(text);
              if (vald === null) return;
              sparning.nollstall();
              sattDag(vald);
            }}
          />
          <Knapp variant="stor" disabled={!kanSpara} onClick={() => void spara()}>
            {sparning.sparar
              ? 'Sparar ...'
              : `Spara ${orenDuger && oren !== null ? visaOren(oren) : 'utlägget'}`}
          </Knapp>
          <Knapp variant="stor-tyst" onClick={() => sattOppet(false)}>
            Avbryt
          </Knapp>
          {kraverOrder && !orderDuger && (
            <Tomtext>Välj vilket jobb utlägget hör till, annars går det inte att fakturera.</Tomtext>
          )}
          {ordrar.length === 0 && kraverOrder && (
            <Tomtext>
              Firman har inga arbetsordrar än. Ett utlägg som inte ska
              vidarefaktureras går att spara ändå — välj "Nej" ovan.
            </Tomtext>
          )}
        </Yta>
      )}

      {sparning.fel !== null && <Felruta fel={sparning.fel} />}
      {sparning.fallna.map((rad) => (
        <Felruta key={rad.op_id} fel={felAvOperation(rad)} />
      ))}

      {oppet && !farSkriva && rapporterar && !arLast && (
        <Tomtext>
          Veckan för den valda dagen är {VECKOLAGETEXT[lage].toLowerCase()} och kan inte
          ändras. Välj en annan dag eller be chefen öppna veckan.
        </Tomtext>
      )}
      {arLast && (
        <Tomtext>
          Prenumerationen är pausad. Allt går att läsa och exportera, men inget nytt
          kan sparas förrän betalningen är ordnad.
        </Tomtext>
      )}

      <Tomtext>
        Utlägg går två vägar: tillbaka till dig, och ut på fakturan som kostnad på
        jobbet om du valt det. Material som tas ur lagret går bara den andra vägen
        och läggs in på arbetsordern.
      </Tomtext>
    </>
  );
};
